import type { Processor } from "unified";
import type { Code, Construct, State, Token, Tokenizer } from "micromark-util-types";
import { labelEnd } from "micromark-core-commonmark";
import { workspaceFileHref } from "./workspaceLinks";

/** Accept horizontal spaces in session file destinations through the standard link resolver. */
export function remarkWorkspaceLinks(this: Processor, { sessionId }: { sessionId?: string }) {
	if (!sessionId) return;
	const resource: Construct = { tokenize: destination(sessionId) };
	const tokenize: Tokenizer = function(effects, ok, nok) {
		const attempt: typeof effects.attempt = (constructs, success, failure) => {
			const standard = effects.attempt(constructs, success, failure);
			return (code) => code === 40 && this._labelStarts?.at(-1)?.type === "labelLink"
				? effects.attempt([...(Array.isArray(constructs) ? constructs : [constructs as Construct]), resource], success, failure)(code)
				: standard(code);
		};
		return labelEnd.tokenize.call(this, { ...effects, attempt }, ok, nok);
	};
	const data = this.data();
	(data.micromarkExtensions ??= []).push({ text: { 93: { ...labelEnd, name: "workspaceLabelEnd", tokenize } }, disable: { null: ["labelEnd"] } });
}

function destination(sessionId: string): Tokenizer {
	return function(effects, ok, nok) {
		const thisContext = this;
		let token: Token;
		let spaced = false;
		let balance = 0;
		return start;

		function start(code: Code): State | undefined {
			effects.enter("resource");
			effects.enter("resourceMarker");
			effects.consume(code);
			effects.exit("resourceMarker");
			return first;
		}

		function first(code: Code): State | undefined {
			if (code === null || code < 0 || code === 32 || code === 60 || code === 41) return nok(code);
			effects.enter("resourceDestination");
			effects.enter("resourceDestinationRaw");
			token = effects.enter("resourceDestinationString");
			effects.enter("chunkString", { contentType: "string" });
			return inside(code);
		}

		function inside(code: Code): State | undefined {
			if (code === 41 && balance === 0) {
				effects.exit("chunkString");
				effects.exit("resourceDestinationString");
				if (!spaced || !workspaceFileHref(sessionId, thisContext.sliceSerialize(token))) return nok(code);
				effects.exit("resourceDestinationRaw");
				effects.exit("resourceDestination");
				effects.enter("resourceMarker");
				effects.consume(code);
				effects.exit("resourceMarker");
				effects.exit("resource");
				return ok;
			}
			if (code === null || code === -5 || code === -4 || code === -3 || code === 92 || code === 60 || code === 62 || code === 34 || code === 39) return nok(code);
			if (code === 40 && ++balance > 32) return nok(code);
			if (code === 41) balance--;
			if (code === 32 || code === -2) spaced = true;
			effects.consume(code);
			return inside;
		}
	};
}
